/**
 * SKU编辑/创建抽屉组件
 */
import React, { useEffect } from 'react';
import { Drawer, Form, Space, Button, Tag, FormInstance } from 'antd';
import { SaveOutlined, ClockCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { SKUFormBasicFields } from '../SKUFormBasicFields';
import { SKUFormSupplyChainFields } from '../SKUFormSupplyChainFields';
import { SKUFormInventoryFields } from '../SKUFormInventoryFields';
import { SKUFormLogisticsFields } from '../SKUFormLogisticsFields';
import { SKU } from '../../../types/core-types';
import { BrandSimple } from '../../../services/brandService';

interface SKUDrawerProps {
    visible: boolean;
    editingSKU: SKU | null;
    brands: BrandSimple[];
    brandMode: 'select' | 'custom';
    autoSaveState: {
        isSaving: boolean;
        lastSavedAt: Date | null;
        hasUnsavedChanges: boolean;
    };
    onClose: () => void;
    onSubmit: () => void;
    onSaveNow: () => void;
    onBrandModeChange: (mode: 'select' | 'custom') => void;
    onValuesChange: (changedValues: any, allValues: any) => void;
    form: FormInstance;
}

export const SKUDrawer: React.FC<SKUDrawerProps> = ({
    visible,
    editingSKU,
    brands,
    brandMode,
    autoSaveState,
    onClose,
    onSubmit,
    onSaveNow,
    onBrandModeChange,
    onValuesChange,
    form
}) => {
    // 当打开编辑模式时，填充表单
    useEffect(() => {
        if (visible && editingSKU) {
            // 卖点数组保持数组格式，如果为空则初始化为一个空项
            const sellingPoints = editingSKU.selling_points && editingSKU.selling_points.length > 0
                ? editingSKU.selling_points
                : [''];

            // 处理站点关键词：将数组格式转换为表单对象格式
            const siteKeywordsObj: Record<string, { keywords: string }> = {};
            if (editingSKU.site_keywords && Array.isArray(editingSKU.site_keywords)) {
                editingSKU.site_keywords.forEach((item) => {
                    siteKeywordsObj[item.site] = {
                        keywords: item.keywords || '',
                    };
                });
            }

            form.setFieldsValue({
                name: editingSKU.name,
                name_cn: editingSKU.name_cn,
                name_en: editingSKU.name_en,
                asin: editingSKU.asin,
                brand_name: editingSKU.brand_name,
                brand_mode: editingSKU.brand_obj ? 'select' : 'custom',
                brand_obj: editingSKU.brand_obj,
                brand: editingSKU.brand,
                category: editingSKU.category,
                selling_points: sellingPoints,
                description: editingSKU.description,
                site_keywords: siteKeywordsObj,
            });
        } else if (visible && !editingSKU) {
            form.resetFields();
            // 新建时初始化一个空卖点
            form.setFieldsValue({ selling_points: [''] });
        }
    }, [visible, editingSKU, form]);

    return (
        <Drawer
            title={
                <Space>
                    {editingSKU ? '编辑SKU' : '新建SKU'}
                    {editingSKU && (
                        <>
                            {autoSaveState.isSaving && (
                                <Tag icon={<SaveOutlined spin />} color="processing" style={{ borderRadius: 12 }}>
                                    保存中...
                                </Tag>
                            )}
                            {autoSaveState.lastSavedAt && !autoSaveState.isSaving && !autoSaveState.hasUnsavedChanges && (
                                <Tag icon={<ClockCircleFilled />} color="success" style={{ borderRadius: 12 }}>
                                    已保存
                                </Tag>
                            )}
                            {autoSaveState.hasUnsavedChanges && !autoSaveState.isSaving && (
                                <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 12 }}>
                                    有未保存的更改
                                </Tag>
                            )}
                        </>
                    )}
                </Space>
            }
            open={visible}
            onClose={onClose}
            width={720}
            placement="right"
            className="sku-drawer"
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onClose} style={{ borderRadius: 8 }}>
                        取消
                    </Button>
                    {editingSKU && (
                        <Button
                            icon={<SaveOutlined />}
                            onClick={onSaveNow}
                            loading={autoSaveState.isSaving}
                            style={{ borderRadius: 8 }}
                        >
                            立即保存
                        </Button>
                    )}
                    <Button type="primary" onClick={onSubmit} style={{ borderRadius: 8 }}>
                        {editingSKU ? '保存并关闭' : '创建'}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    brand_mode: brandMode,
                }}
                onValuesChange={onValuesChange}
            >
                <SKUFormBasicFields
                    brands={brands}
                    brandMode={brandMode}
                    onBrandModeChange={onBrandModeChange}
                    isEditing={!!editingSKU}
                />

                <SKUFormSupplyChainFields />

                <SKUFormInventoryFields />

                <SKUFormLogisticsFields />
            </Form>
        </Drawer>
    );
};
